import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, RotateCcw } from "lucide-react";
import { api, BACKEND } from "@/lib/api";
import { HERO_IMG, PREVIEW_IMG } from "@/pages/freelanding/copy";

const SLOTS = [
  ["free_hero", "Free landing (/free) — hero image", HERO_IMG],
  ["free_preview", "Free landing (/free) — Mobile Car Wash preview", PREVIEW_IMG],
  ["welcome_hero", "Paid landing (/welcome) — hero image", null],
];
const TYPES = ["image/jpeg", "image/png", "image/webp"];
const err = (e) => e?.response?.data?.detail || "Upload failed. Please try again.";

function Slot({ slot, label, def, url, onChange }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!TYPES.includes(f.type)) return toast.error("Please choose a JPG, PNG or WebP image.");
    if (f.size > 5 * 1024 * 1024) return toast.error("The image is larger than 5 MB.");
    setBusy(true);
    const fd = new FormData();
    fd.append("file", f);
    try { onChange((await api.post(`/admin/landing-images/${slot}`, fd)).data); toast.success("Image updated"); } catch (er) { toast.error(err(er)); } finally { setBusy(false); }
  };
  const reset = async () => {
    setBusy(true);
    try { onChange((await api.delete(`/admin/landing-images/${slot}`)).data); toast.success("Default image restored"); } catch (er) { toast.error(err(er)); } finally { setBusy(false); }
  };
  const src = url ? `${BACKEND}${url}` : def;
  return (
    <div className="rounded-2xl border border-[#EADEC9] p-4" data-testid={`landing-image-${slot}`}>
      <div className="font-semibold text-sm">{label}</div>
      <div className="mt-3 aspect-[3/2] rounded-xl overflow-hidden bg-[#F4F8F9] flex items-center justify-center">
        {src ? <img src={src} alt={label} className="w-full h-full object-cover" data-testid={`landing-image-preview-${slot}`} /> : <span className="text-xs text-[#8C9BA5] px-4 text-center">Default: the designed guide preview (no photo)</span>}
      </div>
      <div className="text-xs mt-2 font-semibold" data-testid={`landing-image-status-${slot}`}>{url ? <span className="text-[#179B80]">Custom image</span> : <span className="text-[#8C9BA5]">Default image</span>}</div>
      <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={pick} data-testid={`landing-image-input-${slot}`} />
      <div className="flex flex-wrap gap-2 mt-3">
        <button disabled={busy} onClick={() => ref.current?.click()} className="sb-btn-primary h-10 text-sm" data-testid={`landing-image-upload-${slot}`}><Upload className="h-4 w-4" />{busy ? "Working…" : "Upload new image"}</button>
        {url && <button disabled={busy} onClick={reset} className="sb-btn-ghost h-10 text-sm" data-testid={`landing-image-reset-${slot}`}><RotateCcw className="h-4 w-4" />Restore default</button>}
      </div>
    </div>
  );
}

export function LandingImages() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/public/landing-images").then((r) => setD(r.data)).catch(() => setD({})); }, []);
  return (
    <section className="sb-card p-5" data-testid="landing-images-admin">
      <h2 className="font-display font-extrabold text-lg">Landing images</h2>
      <p className="text-sm text-[#5C6B73] mt-1">JPG, PNG or WebP, max 5 MB. Wide photos (3:2) look best. Changes show on the live pages right away.</p>
      <div className="grid md:grid-cols-3 gap-4 mt-4">
        {SLOTS.map(([k, l, def]) => <Slot key={k} slot={k} label={l} def={def} url={d?.[k]} onChange={setD} />)}
      </div>
    </section>
  );
}
